/**
 * Turning on and turning off, day by day.
 *
 * Two bars per day rather than a net line: a day where 50 people joined and 50 left is not a quiet
 * day, and a single line would draw it as one.
 *
 * Drawn by `UTGroupedBarChart`, the design system's Recharts wrapper, rather than by hand. The
 * hand-rolled version this replaces was `div`s with percentage heights — which drew the bars fine
 * but had no hover, so the only way to read a value was to guess it from the height. The shared
 * component brings the tooltip, the grid, the legend and the hover highlight with it.
 *
 * @module Pages/BUs/OptInFeatures
 */

import { css } from '@emotion/css';
import { UTGroupedBarChart } from '@group-link-one/gl-ui/utilities';
import type { IUTGroupedBarChart } from '@group-link-one/gl-ui/utilities';
import type { JSX } from 'react';
import type { OptInAdoptionPoint } from '../../../Types/OptInFeatures.types';
import { formatDayToggles, formatSeriesDate } from '../../../Utils/adoptionFormat';

/*
  Same tokens the metric cards use for the two directions, so a green bar and the green "Ligados
  agora" card are the same green. They reach Recharts as an SVG `fill`, which resolves CSS custom
  properties like any other paint.
*/
const ENABLED_FILL = 'var(--color-success)';
const DISABLED_FILL = 'var(--color-danger)';

const emptyStyle = css`
  font-size: var(--font-size-sm);
  color: var(--color-text-muted);
`;

export interface AdoptionSeriesChartProps {
  series: OptInAdoptionPoint[];
  id?: string;
}

export const AdoptionSeriesChart = ({ series, id }: AdoptionSeriesChartProps): JSX.Element => {
  if (series.length === 0) {
    return (
      <p id={id} className={emptyStyle}>
        Ainda não houve nenhuma ativação ou desativação.
      </p>
    );
  }

  const data: IUTGroupedBarChart['data'] = series.map(point => ({
    /*
      `subInfo` is the tooltip's one extra row, and it carries both things the bars cannot say: the
      day, and the toggles behind the people.

      The day, because the tooltip prints the bars alone — name and value, no header — so hovering
      a dense series would say "12 ligaram" with no way to tell which day, and the axis label under
      the cursor is exactly what the box covers.

      The toggles, because the bars count people: without this, a day where one person flipped
      three times looks identical to a quiet one. Split by action, since a single total sits next
      to two bars reading `1` and `1` and invites the reader to add them.
    */
    category: {
      id: point.date,
      label: formatSeriesDate(point.date),
      subInfo: { name: formatSeriesDate(point.date), value: formatDayToggles(point) },
    },
    /*
      The labels name their unit — "Pessoas que ligaram", not "Ligaram".

      Without it the tooltip reads `1` and `1` above `5 eventos de 1 pessoa`, and the eye adds the
      two bars and demands they make 5. They never will: the bars count people and the line counts
      toggles, and the same person can be on both sides of one day. The label is the only place
      that can say so, since the tooltip floats free of the panel title that says it once.

      The legend takes the same strings — longer than before, and worth it: the tooltip is read in
      isolation, the legend next to a title that already agrees with it.
    */
    bars: [
      { id: 'enabled', label: 'Pessoas que ligaram', value: point.enabled, fill: ENABLED_FILL },
      {
        id: 'disabled',
        label: 'Pessoas que desligaram',
        value: point.disabled,
        fill: DISABLED_FILL,
      },
    ],
  }));

  return (
    <div id={id}>
      {/*
        No `height`: while `responsive` is on — and it is, by default — the chart takes `100%` with a
        300px floor and ignores the prop entirely. Passing one would read like a setting that works.
      */}
      <UTGroupedBarChart data={data} showTooltip showLegend showGrid />
    </div>
  );
};
